import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking; false = unauthenticated; object = authenticated
  const [user, setUser] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);

  const loadMemberships = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/memberships");
      setMemberships(data || []);
    } catch {
      setMemberships([]);
    } finally {
      setMembershipsLoaded(true);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadMemberships();
    } catch {
      setUser(false);
      setMemberships([]);
      setMembershipsLoaded(true);
    }
  }, [loadMemberships]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      setMembershipsLoaded(false);
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await loadMemberships();
      return { ok: true, user: data };
    } catch (e) {
      setMembershipsLoaded(true);
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      setMembershipsLoaded(false);
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      await loadMemberships();
      return { ok: true, user: data };
    } catch (e) {
      setMembershipsLoaded(true);
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
    setMemberships([]);
  };

  const switchTeam = async (teamId) => {
    try {
      const { data } = await api.post("/auth/switch-team", { team_id: teamId });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      memberships,
      membershipsLoaded,
      login,
      register,
      logout,
      refresh,
      loadMemberships,
      switchTeam,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
